/*! @license MIT
 * Copyright (c) 2025 Nous Research
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
 * THE SOFTWARE.
 */
/** Adapted from Hermes gateway/slash_commands_model.py display/effort selection
 * and apps/desktop/src/lib/reasoning-slash.ts scope parsing. Durable scope and
 * validation replace single-user YAML writes; display remains per client. */
import { isReasoningEffort, type ReasoningEffort } from "@dayvise/domain";

export const REASONING_USAGE = "Usage: `/reasoning [none|minimal|low|medium|high|xhigh|max|ultra|reset] [--session|--global]`, or `/reasoning show|hide` for this client's display. Global changes require agent-owner or shared-agent administrator access.";
export type ReasoningCommandArgs =
  | { readonly kind: "status" }
  | { readonly kind: "display"; readonly show: boolean }
  | { readonly kind: "effort"; readonly effort: ReasoningEffort | null; readonly global: boolean }
  | { readonly kind: "error"; readonly error: string };

export function parseReasoningCommandArgs(raw: string): ReasoningCommandArgs {
  if (!raw.trim()) return { kind: "status" };
  const values: string[] = [];
  let global = false, session = false;
  for (const token of raw.toLowerCase().replace(/[\u2012\u2013\u2014\u2015](global|session)/g, "--$1").trim().split(/\s+/)) {
    if (["--global", "-g", "global"].includes(token)) global = true;
    else if (["--session", "-s", "session"].includes(token)) session = true;
    else values.push(token);
  }
  if (global && session || values.length !== 1) return { kind: "error", error: REASONING_USAGE };
  const value = values[0]!;
  if (["show", "on", "hide", "off"].includes(value)) return global || session
    ? { kind: "error", error: "Display is a preference for this signed-in user in this client. Use `/reasoning show` or `/reasoning hide` without a scope flag." }
    : { kind: "display", show: value === "show" || value === "on" };
  if (value === "reset") return global
    ? { kind: "error", error: "Use `/reasoning reset` to restore the agent's effort default for this branch. A global reset is not supported; select a global level explicitly." }
    : { kind: "effort", effort: null, global: false };
  const effort = ["false", "disabled"].includes(value) ? "none" : value;
  return isReasoningEffort(effort) ? { kind: "effort", effort, global } : { kind: "error", error: REASONING_USAGE };
}
